import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { and, eq } from 'ember-truth-helpers';

interface GeneralNavigationIntercomHelpLinkSignature {
  Args: {
    articleLink: string;
    iconName?: IconName;
    iconPlacement?: 'before' | 'after';
    isStandaloneLink?: boolean;
    linkText?: string;
    onHelpArticleOpened?: (articleLink: string) => void;
    textSize?: 'small' | 'medium' | 'large';
    tooltipText?: string;
    type: 'text' | 'text-and-icon' | 'icon';
  };
  Element: HTMLButtonElement;
}

export default class GeneralNavigationIntercomHelpLink extends Component<GeneralNavigationIntercomHelpLinkSignature> {
  @service declare intercom: Services['intercom'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconPlacement() {
    return this.args.iconPlacement || 'after';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get textSize() {
    return this.args.textSize || 'medium';
  }

  @action openHelpArticle(): void {
    this.intercom.redirectToIntercomHelp(this.args.articleLink);

    this.args.onHelpArticleOpened?.(this.args.articleLink);
  }

  <template>
    {{#if (eq @type "text")}}
      <button
        class="link-button--unstyled prism-link prism-link--{{this.textSize}}
          {{if @isStandaloneLink 'prism-link--standalone'}}"
        type="button"
        {{on "click" this.openHelpArticle}}
        ...attributes
      >
        {{@linkText}}
      </button>
    {{/if}}

    {{#if (eq @type "icon")}}
      <button
        class="button--unstyled"
        type="button"
        {{on "click" this.openHelpArticle}}
        ...attributes
      >
        {{#if @iconName}}
          <div>
            <PrismIcon @color="inherit" @name={{@iconName}} @size="small" />
            {{#if @tooltipText}}
              <PrismTooltip @side="top" @text={{@tooltipText}} />
            {{/if}}
          </div>
        {{/if}}
      </button>
    {{/if}}

    {{#if (eq @type "text-and-icon")}}
      <button
        class="link-button--unstyled prism-link prism-link--{{this.textSize}}
          inline-flex items-center gap-100
          {{if @isStandaloneLink 'prism-link--standalone'}}"
        type="button"
        {{on "click" this.openHelpArticle}}
        ...attributes
      >
        {{#if (and @iconName (eq this.iconPlacement "before"))}}
          {{! @glint-expect-error: Incorrect type }}
          <PrismIcon @color="inherit" @name={{@iconName}} @size="small" />
        {{/if}}

        <span
          class="prism-link prism-link--{{this.textSize}}
            {{if @isStandaloneLink 'prism-link--standalone'}}"
        >{{@linkText}}</span>

        {{#if (and @iconName (eq this.iconPlacement "after"))}}
          {{! @glint-expect-error: Incorrect type }}
          <PrismIcon @color="inherit" @name={{@iconName}} @size="small" />
        {{/if}}
      </button>
    {{/if}}
  </template>
}
